import * as React from 'react';
import { ACTION_CHECKOUT_SAVE_ADDONS } from 'utils/analytics/eventTypeConstantsAmplitude';
import { Head } from 'head/Head';
import {
  OptionsRouteConnectProps,
  OptionsRouteProps,
} from 'routes/business/tableside-flow/OptionsRoute';
import { core, requestToJSON, safeToNumber } from '@tock/coretypes';
import { exposedErrorCodes } from '@tock/error-codes';
import { generateOptionDiff } from '@tock/shared/utils/option';
import { includes } from 'es-toolkit/compat';
import { isSourceTockWidget } from 'utils/checkoutUtils';
import { lib } from 'types';
import { useActions } from '@tock/shared/utils/connect';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useMountEffect } from '@tock/hooks';
import AddonSelection from 'components/business/addons/AddonSelection';
import Section from 'components/Section';

interface OwnProps {
  fetchAndValidateCardsPresent?(): void;
}

type OptionsProps = OptionsRouteProps & OptionsRouteConnectProps & OwnProps;

const Options: React.FunctionComponent<OptionsProps> = ({
  app: { config, patron },
  business: businessConfig,
  lock: { currentLock },
  supplementGroups,
  purchase: { calculatedPrice },
  onUpdatePrice,
  optionOrders,
  originalOptionOrders,
  partyOptionOrders,
  nextRoute,
  onPurchaseSuccess,
  visitEditToken,
  basePurchaseId,
  checkoutOrigin,
  fetchAndValidateCardsPresent,
}) => {
  const appBusiness = config?.business;
  const business = appBusiness || businessConfig;
  const { actions } = useActions();
  const history = useHistory();
  const isFetchWidgetTicketLocksEnabled = useFeatureFlag('FETCH_WIDGET_TICKET_LOCKS');
  const { search } = useLocation();

  useMountEffect(() => {
    actions.checkout.setCheckoutStatus('Add-ons');
    if (
      isFetchWidgetTicketLocksEnabled &&
      business &&
      business?.timeZone &&
      business?.id &&
      isSourceTockWidget(search)
    ) {
      actions.lock
        .fetchLockForBusiness(business?.timeZone, business.id)
        .then(() => onUpdatePrice());
    } else {
      onUpdatePrice();
    }
  });
  const amplitudeSendEvent = useAmplitudeSendEvent();

  const handleConfirm = () => {
    amplitudeSendEvent(ACTION_CHECKOUT_SAVE_ADDONS);

    history.push(nextRoute);
  };

  const handleSubmitOrder = () => {
    if (!originalOptionOrders || !optionOrders || !basePurchaseId || !visitEditToken) {
      return Promise.reject();
    }
    const { addOptions, removeOptions } = generateOptionDiff(optionOrders, originalOptionOrders);
    return requestToJSON<core.PurchaseOrder>(
      actions.purchase.submitOptionsToPurchase(
        addOptions,
        removeOptions,
        basePurchaseId,
        visitEditToken
      )
    )
      .then(onPurchaseSuccess)
      .catch(handleError);
  };

  const handleError = (response: { error: lib.ErrorMessageProto }) => {
    if (!response?.error?.errorCode) {
      actions.toast.showError('There was a problem submitting your order.');
      return;
    }
    const errorCode = safeToNumber(response.error.errorCode);
    if (includes(exposedErrorCodes, errorCode) && !!visitEditToken) {
      actions.toast.showError(response.error.message);
      if (errorCode === 3053) {
        const domainName = business?.domainName;
        history.replace(`/${domainName}/tableside/?token=${encodeURIComponent(visitEditToken)}`);
      } else if (errorCode === 3061) {
        fetchAndValidateCardsPresent && fetchAndValidateCardsPresent();
      }
    } else {
      actions.toast.showError('There was a problem submitting your order.');
    }
  };

  const supplementGroupStates = supplementGroups.supplementGroupStates;
  if (!currentLock || !supplementGroupStates || !calculatedPrice || !business) {
    return null;
  }

  const { ticketType } = currentLock;
  const currentTicketType = ticketType[0];
  if (!currentTicketType) {
    return null;
  }

  return (
    <Section>
      <Head title={`${business?.name} - Add-ons`} />
      <div className="Consumer-smallContentContainer">
        <div className="SupplementGroups">
          <AddonSelection
            business={business}
            ticketOption={currentLock.ticketOption}
            ticketOptionGroup={currentLock.ticketOptionGroup}
            optionPrices={currentLock.option}
            partySize={currentLock.ticketSubset?.[0]?.ticketCount ?? 0}
            optionOrders={optionOrders || []}
            partyOptionOrders={partyOptionOrders}
            ticketType={currentTicketType}
            supplementGroupStates={supplementGroupStates}
            calculatedPrice={calculatedPrice}
            setSupplementGroupStates={actions.supplementGroups.setSupplementGroupStates}
            selectOptions={actions.selection.selectOptions}
            onConfirm={handleConfirm}
            onSubmitOrder={handleSubmitOrder}
            onUpdatePrice={onUpdatePrice}
            patron={patron || undefined}
            checkoutOrigin={checkoutOrigin}
          />
        </div>
      </div>
    </Section>
  );
};

export default Options;
